import React from "react";
import { Select, Typography } from "antd";
import { ErrorState, PageHeader } from "@dowty/ui";
import dayjs from "dayjs";
import { EventSummary } from "../../../services/drinkService";
import { useTranslation } from "react-i18next";

const renderEventOption: NonNullable<React.ComponentProps<typeof Select>["optionRender"]> = (option) => (
    <span>{option.data.label}<br /><span className="event-stats-option-description">{option.data.description}</span></span>
);

type EventStatsHeaderProps = {
    events: EventSummary[];
    selectedEventId: number | null;
    loadingEvents: boolean;
    onEventSelect: (eventId: number | null) => void;
    error: string | null;
    onDismissError: () => void;
    onRetry: () => void;
};

const EventStatsHeader: React.FC<EventStatsHeaderProps> = ({
    events,
    selectedEventId,
    loadingEvents,
    onEventSelect,
    error,
    onDismissError,
    onRetry,
}) => {
    const { t } = useTranslation();

    return (
        <div className="event-stats-header">
                <PageHeader
                    title={t("eventStats.header.title")}
                    subtitle={t("eventStats.header.subtitle")}
                    extra={
                    <>
                    <label className="ui-sr-only" htmlFor="event-stats-event-select">{t("eventStats.header.selectPlaceholder")}</label>
                    <Select
                        id="event-stats-event-select"
                        getPopupContainer={() => document.getElementById("ui-main") ?? document.body}
                        showSearch
                        className="event-stats-event-select"
                        placeholder={t("eventStats.header.selectPlaceholder")}
                        loading={loadingEvents}
                        value={selectedEventId ?? undefined}
                        optionFilterProp="label"
                        options={events.map((evt) => ({
                            value: evt.eventId,
                            label: evt.eventName,
                            description: evt.start
                                ? t("eventStats.header.startedLabel", {
                                      time: dayjs(evt.start).format("MMM D HH:mm"),
                                  })
                                : t("eventStats.header.optionNoStart"),
                        }))}
                        onChange={(value) => {
                            if (value === undefined || value === null) {
                                onEventSelect(null);
                                return;
                            }
                            const numericEventId = Number(value);
                            onEventSelect(Number.isNaN(numericEventId) ? null : numericEventId);
                        }}
                        optionRender={renderEventOption}
                    />
                    </>
                    }
                />
            {error && (
                <div style={{ marginTop: 16 }}>
                    <Typography.Title level={2}>{t("eventStats.header.errorTitle")}</Typography.Title>
                    <ErrorState message={error} retry={onRetry} retryLabel={t("eventStats.header.retryLabel")} />
                    <button type="button" className="event-stats-dismiss-error" onClick={onDismissError}>{t("common.close")}</button>
                </div>
            )}
        </div>
    );
};

export default EventStatsHeader;
